import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle,
  Payment,
  ContentCopy,
  OpenInNew,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import api from '../config/api';
import PublicPackageDetails from './PublicPackageDetails';
import DocumentPageOverlay, { documentOverlayActionSx } from '../components/DocumentPageOverlay';
import NumericTextField from '../components/NumericTextField';
import PartnerBookingDetailsDialog from '../components/PartnerBookingDetailsDialog';
import PartnerTeamMemberFilter from '../components/PartnerTeamMemberFilter';
import { buildAssigneeFilterParams } from '../utils/partnerAssigneeFilter';
import { getPartnerSessionFromToken } from '../utils/partnerPush';
import {
  PartnerEmptyState,
  PartnerFilterBar,
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';
import {
  ALERT,
  bookingsToCsv,
  formatMoney,
  getBookingBucket,
  matchesQuery,
} from '../utils/partnerListUtils';

const PartnerBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const partnerSession = useMemo(() => getPartnerSessionFromToken(), []);
  const { bookingId: bookingIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const packageFilter = searchParams.get('package');
  
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [confirmationId, setConfirmationId] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [paymentLinkGenerated, setPaymentLinkGenerated] = useState(null);
  
  // Booking details modal state
  const [detailsModal, setDetailsModal] = useState(false);
  const [detailsBooking, setDetailsBooking] = useState(null);
  
  // Package preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [openingThreadId, setOpeningThreadId] = useState(null);
  const [teamMemberFilter, setTeamMemberFilter] = useState(
    partnerSession.isPartnerUser ? 'mine' : 'all'
  );

  const assigneeQueryString = useMemo(() => {
    const params = buildAssigneeFilterParams(teamMemberFilter);
    params.set('limit', '500');
    const qs = params.toString();
    return qs ? `?${qs}` : '?limit=500';
  }, [teamMemberFilter]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('partnerToken');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/package-booking/partner/bookings${assigneeQueryString}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setAllBookings(response.data.bookings);
      }
    } catch (err) {
      console.error('Failed to load bookings:', err);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, assigneeQueryString]);

  useEffect(() => {
    if (!localStorage.getItem('partnerToken')) {
      navigate('/partner/login');
      return;
    }
    loadBookings();
  }, [loadBookings, navigate]);

  const openBookingDetails = (booking) => {
    setDetailsBooking(booking);
    setDetailsModal(true);
  };

  const closeBookingDetails = () => {
    setDetailsModal(false);
    if (bookingIdParam) navigate('/partner/bookings', { replace: true });
    if (location.state?.openBookingId) {
      navigate('/partner/bookings', { replace: true, state: {} });
    }
  };

  const resolveAndOpenBooking = useCallback(async (id) => {
    if (!id) return;
    const match = allBookings.find((row) => String(row.id) === String(id));
    if (match) {
      openBookingDetails(match);
      return;
    }
    try {
      const response = await api.get(`/package-booking/partner/bookings/${id}`, {
        headers: getAuthHeaders(),
      });
      if (response.data?.booking) {
        openBookingDetails(response.data.booking);
      }
    } catch (err) {
      console.error('Failed to open booking details:', err);
    }
  }, [allBookings, getAuthHeaders]);

  useEffect(() => {
    if (loading) return;
    const fromState = location.state?.openBookingId;
    const targetId = bookingIdParam || fromState;
    if (!targetId) return;
    resolveAndOpenBooking(targetId);
  }, [bookingIdParam, loading, location.state, resolveAndOpenBooking]);

  const openConfirmDialog = (booking) => {
    setSelectedBooking(booking);
    setConfirmationId('');
    setConfirmNotes('');
    setCustomAmount(booking.total_price.toString());
    setPaymentLinkGenerated(null);
    setConfirmDialog(true);
  };

  const handleConfirmBooking = async () => {
    if (!confirmationId.trim()) {
      setError('Please enter a confirmation ID');
      return;
    }

    try {
      setConfirming(true);
      const response = await api.post(
        `/package-booking/partner/bookings/${selectedBooking.id}/confirm`,
        {
          confirmationId,
          notes: confirmNotes,
          customAmount: customAmount !== selectedBooking.total_price.toString() 
            ? parseFloat(customAmount) 
            : null
        },
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        setPaymentLinkGenerated(response.data.paymentLink);
        setSuccess('Booking confirmed! Payment link sent to customer.');
        
        // Update booking in list
        setAllBookings(prev => prev.map(b => 
          b.id === selectedBooking.id 
            ? { ...b, status: 'partner_confirmed', partner_confirmation_id: confirmationId }
            : b
        ));
      }
    } catch (err) {
      console.error('Failed to confirm booking:', err);
      setError('Failed to confirm booking');
    } finally {
      setConfirming(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const handleViewDetails = (booking) => {
    openBookingDetails(booking);
  };

  const openPackagePreview = (booking) => {
    if (booking?.package_slug) {
      setPreviewSlug(booking.package_slug);
      setPreviewOpen(true);
      return;
    }
    if (booking?.package_id) {
      navigate(`/partner/packages/${booking.package_id}/edit`);
    }
  };

  const customerMailto = (booking) => {
    if (!booking?.user_email) return null;
    const subject = encodeURIComponent(
      [booking.booking_reference, booking.package_title].filter(Boolean).join(' — ')
    );
    return `mailto:${booking.user_email}?subject=${subject}`;
  };

  const openInquiry = async (booking) => {
    if (!booking) return;
    if (booking.inquiry_id) {
      navigate(`/partner/inquiries/${booking.inquiry_id}`);
      return;
    }
    if (openingThreadId) return;

    setOpeningThreadId(booking.id);
    try {
      const response = await api.post(
        `/package-booking/partner/bookings/${booking.id}/thread`,
        {},
        { headers: getAuthHeaders() }
      );
      const inquiryId = response.data?.inquiryId;
      if (!inquiryId) {
        setError(t('partnerLists.messageThreadFailed'));
        return;
      }

      setAllBookings((prev) => prev.map((row) => (
        row.id === booking.id ? { ...row, inquiry_id: inquiryId } : row
      )));
      if (detailsBooking?.id === booking.id) {
        setDetailsBooking((prev) => (prev ? { ...prev, inquiry_id: inquiryId } : prev));
      }
      navigate(`/partner/inquiries/${inquiryId}`);
    } catch (err) {
      console.error('Failed to open booking thread:', err);
      setError(t('partnerLists.messageThreadFailed'));
    } finally {
      setOpeningThreadId(null);
    }
  };

  const closePackagePreview = () => {
    setPreviewOpen(false);
    setPreviewSlug(null);
  };

  // Helper to parse passengers from booking
  const getPassengers = (booking) => {
    if (!booking?.travelers) return [];
    try {
      if (typeof booking.travelers === 'string') {
        return JSON.parse(booking.travelers);
      }
      return Array.isArray(booking.travelers) ? booking.travelers : [];
    } catch (e) {
      return [];
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return t('partnerLists.statusAwaiting');
      case 'partner_confirmed': return t('partnerLists.statusConfirmedPaid');
      case 'payment_pending': return t('partnerLists.statusConfirmedPaid');
      case 'paid': return t('partnerLists.statusPaid');
      case 'confirmed':
      case 'completed': return t('partnerLists.statusComplete');
      case 'cancelled': return t('partnerLists.statusCancelled');
      default: return status;
    }
  };

  const packageFilteredBookings = packageFilter 
    ? allBookings.filter(b => b.package_id === parseInt(packageFilter))
    : allBookings;

  const searchedBookings = packageFilteredBookings.filter((booking) => matchesQuery([
    booking.booking_reference,
    booking.user_name,
    booking.user_email,
    booking.package_title,
  ], search));

  const statusCounts = {
    all: searchedBookings.length,
    awaiting: searchedBookings.filter(b => getBookingBucket(b.status) === 'awaiting').length,
    confirmed: searchedBookings.filter(b => getBookingBucket(b.status) === 'confirmed').length,
    complete: searchedBookings.filter(b => getBookingBucket(b.status) === 'complete').length,
  };

  const bookings = activeTab === 'all'
    ? searchedBookings
    : searchedBookings.filter((booking) => getBookingBucket(booking.status) === activeTab);

  const thisYear = new Date().getFullYear();
  const yearBookings = packageFilteredBookings.filter((booking) => (
    booking.created_at && new Date(booking.created_at).getFullYear() === thisYear
  ));
  const totals = {
    gross: yearBookings.reduce((sum, booking) => sum + Number(booking.total_price || 0), 0),
    payout: yearBookings.reduce((sum, booking) => sum + Number(booking.partner_payout_amount || 0), 0),
    commission: yearBookings.reduce((sum, booking) => sum + Number(booking.commission_amount || 0), 0),
    awaiting: packageFilteredBookings
      .filter((booking) => booking.status === 'pending')
      .reduce((sum, booking) => sum + Number(booking.total_price || 0), 0),
  };

  const travelerLine = (booking) => {
    const passengers = getPassengers(booking);
    const names = passengers.map((passenger) => passenger.firstName || passenger.first_name).filter(Boolean);
    if (names.length > 2) {
      return t('partnerLists.plusMore', { name: names[0], count: names.length - 1 });
    }
    if (names.length) return names.join(', ');
    return t('partnerLists.travellers', { count: booking.num_travelers || 1 });
  };

  const travelDates = (booking) => {
    if (!booking.travel_date) return t('partnerLists.notSet');
    const start = new Date(booking.travel_date);
    if (!booking.return_date) return format(start, 'd MMM yyyy');
    const end = new Date(booking.return_date);
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `${format(start, 'd')}–${format(end, 'd MMM yyyy')}`;
    }
    if (start.getFullYear() === end.getFullYear()) {
      return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
    }
    return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
  };

  const exportCsv = () => {
    const blob = new Blob([bookingsToCsv(bookings)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'partner-bookings.csv';
    link.click();
    URL.revokeObjectURL(url);
  };


  const tdSx = {
    py: 2,
    px: 2,
    borderBottom: `1px solid ${DOCUMENT.rule}`,
    fontSize: 13.5,
    verticalAlign: 'top',
  };
  const primarySx = { fontWeight: 400, fontSize: 13.5, display: 'block' };
  const metaSx = { fontSize: 11.5, color: DOCUMENT.ink3, display: 'block', mt: 0.35 };
  const textLinkSx = {
    ...primarySx,
    background: 'none',
    border: 0,
    p: 0,
    cursor: 'pointer',
    textAlign: 'left',
    color: DOCUMENT.ink,
    textDecoration: 'underline',
    textDecorationColor: DOCUMENT.ruleHard,
    '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
  };
  const mailLinkSx = {
    ...metaSx,
    color: DOCUMENT.ink2,
    textDecoration: 'underline',
    textDecorationColor: DOCUMENT.ruleHard,
    '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
  };

  const actionLinkSx = { ...partnerLinkSx, whiteSpace: 'nowrap' };
  const renderConfirmButton = (booking) => (
    booking.status === 'pending' ? (
      <Button
        sx={{ ...partnerOxbloodBtnSx, flexShrink: 0, mt: 1, px: 1.75, py: 0.9 }}
        onClick={() => openConfirmDialog(booking)}
      >
        {t('partnerLists.confirm')}
      </Button>
    ) : null
  );
  const renderRowActions = (booking) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        gap: 1.25,
        flexWrap: 'nowrap',
      }}
    >
      <Box component="button" sx={actionLinkSx} onClick={() => handleViewDetails(booking)}>
        {t('partnerLists.viewDetails')}
      </Box>
      <Box
        component="button"
        sx={{
          ...actionLinkSx,
          opacity: openingThreadId === booking.id ? 0.5 : 1,
          pointerEvents: openingThreadId === booking.id ? 'none' : 'auto',
        }}
        onClick={() => openInquiry(booking)}
      >
        {t('partnerLists.messageInquiry')}
      </Box>
    </Box>
  );

  return (
    <PartnerPageShell
      error={error}
      onClearError={() => setError(null)}
      success={success}
      onClearSuccess={() => setSuccess(null)}
    >
      <PartnerPageHeader
        title={t('partnerLists.bookingsTitle')}
        subtitle={t('partnerLists.bookingsSub')}
        actions={
          <Box component="button" sx={partnerLinkSx} onClick={exportCsv}>
            {t('partnerLists.exportCsv')}
          </Box>
        }
      />

      {packageFilter && packageFilteredBookings.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 2 }}>
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
            {t('partnerLists.packageFilter', { title: packageFilteredBookings[0]?.package_title || `#${packageFilter}` })}
          </Typography>
          <Box component="button" sx={partnerLinkSx} onClick={() => navigate('/partner/bookings')}>
            {t('partnerLists.clearFilter')}
          </Box>
        </Box>
      )}

      <PartnerTeamMemberFilter
        value={teamMemberFilter}
        onChange={setTeamMemberFilter}
        isPartnerUser={partnerSession.isPartnerUser}
        getAuthHeaders={getAuthHeaders}
        testId="partner-bookings-team-filter"
      />

      <PartnerFilterBar
        chips={[
          { id: 'all', label: t('partnerLists.filterAll'), count: statusCounts.all },
          { id: 'awaiting', label: t('partnerLists.filterAwaiting'), count: statusCounts.awaiting },
          { id: 'confirmed', label: t('partnerLists.filterConfirmed'), count: statusCounts.confirmed },
          { id: 'complete', label: t('partnerLists.filterComplete'), count: statusCounts.complete },
        ]}
        activeChip={activeTab}
        onChipChange={setActiveTab}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('partnerLists.searchBookings')}
      />

      {loading ? (
        <PartnerLoading />
      ) : bookings.length === 0 ? (
        <PartnerEmptyState
          title={t('partnerLists.noBookings')}
          body={t('partnerLists.noBookingsBody')}
        />
      ) : (
        <>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              mt: 1,
              display: { xs: 'none', md: 'table' },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                {[t('partnerLists.colReference'), t('partnerLists.colCustomer'), t('partnerLists.colPackage'), t('partnerLists.colTravellers'), t('partnerLists.colDates'), t('partnerLists.colGross'), t('partnerLists.colPayout'), t('partnerLists.colStatus'), ''].map((label, index) => (
                  <Box
                    key={`${label}-${index}`}
                    component="th"
                    sx={{
                      textAlign: index === 5 || index === 6 || index === 8 ? 'right' : 'left',
                      fontSize: 10,
                      fontWeight: 500,
                      letterSpacing: '0.19em',
                      textTransform: 'uppercase',
                      color: DOCUMENT.ink3,
                      pt: 1.9,
                      pb: 1.35,
                      px: 2,
                      pl: index === 0 ? 0 : 2,
                      pr: index === 8 ? 0 : 2,
                      borderBottom: `1px solid ${DOCUMENT.rule}`,
                      whiteSpace: index === 4 || index === 7 ? 'nowrap' : 'normal',
                      width: index === 8 ? '1%' : 'auto',
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {bookings.map((booking) => (
                <Box component="tr" key={booking.id} data-testid="partner-booking-row">
                  <Box component="td" sx={tdSx}>
                    <Box
                      component="button"
                      data-testid="booking-details-link"
                      onClick={() => handleViewDetails(booking)}
                      sx={textLinkSx}
                    >
                      {booking.booking_reference}
                    </Box>
                    <Typography sx={metaSx}>{booking.created_at ? format(new Date(booking.created_at), 'd MMM yyyy') : ''}</Typography>
                  </Box>
                  <Box component="td" sx={tdSx}>
                    <Typography sx={primarySx}>{booking.user_name}</Typography>
                    {booking.user_email ? (
                      <Box
                        component="a"
                        href={customerMailto(booking)}
                        data-testid="booking-email-link"
                        sx={mailLinkSx}
                      >
                        {booking.user_email}
                      </Box>
                    ) : null}
                    {booking.assigned_partner_user_name ? (
                      <Typography sx={metaSx}>
                        {t('partnerLists.forTeamMember', { name: booking.assigned_partner_user_name })}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box component="td" sx={tdSx}>
                    <Box
                      component="button"
                      data-testid="booking-package-link"
                      onClick={() => openPackagePreview(booking)}
                      sx={textLinkSx}
                    >
                      {booking.package_title}
                    </Box>
                    <Typography sx={metaSx}>{booking.destination}</Typography>
                  </Box>
                  <Box component="td" sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums' }}>
                    <Typography sx={primarySx}>{booking.num_travelers || 1}</Typography>
                    <Typography sx={metaSx}>{travelerLine(booking)}</Typography>
                  </Box>
                  <Box
                    component="td"
                    sx={{ ...tdSx, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                  >
                    {travelDates(booking)}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(booking.total_price)}</Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(booking.partner_payout_amount)}</Box>
                  <Box component="td" sx={{ ...tdSx, whiteSpace: 'nowrap' }}>
                    <Typography sx={{ fontSize: 12.5, color: booking.status === 'pending' ? ALERT : DOCUMENT.ink2, whiteSpace: 'nowrap' }}>
                      {getStatusLabel(booking.status)}
                    </Typography>
                    {renderConfirmButton(booking)}
                  </Box>
                  <Box component="td" sx={{ ...tdSx, textAlign: 'right', pr: 0, width: '1%', whiteSpace: 'nowrap' }}>
                    {renderRowActions(booking)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 2, mt: 1 }}>
            {bookings.map((booking) => (
              <Box key={booking.id} sx={{ py: 2, borderBottom: `1px solid ${DOCUMENT.rule}` }} data-testid="partner-booking-row">
                <Box
                  component="button"
                  onClick={() => handleViewDetails(booking)}
                  sx={textLinkSx}
                >
                  {booking.booking_reference}
                </Box>
                <Typography sx={metaSx}>{booking.user_name}</Typography>
                {booking.assigned_partner_user_name ? (
                  <Typography sx={metaSx}>
                    {t('partnerLists.forTeamMember', { name: booking.assigned_partner_user_name })}
                  </Typography>
                ) : null}
                {booking.user_email ? (
                  <Box component="a" href={customerMailto(booking)} sx={mailLinkSx}>{booking.user_email}</Box>
                ) : null}
                <Box
                  component="button"
                  onClick={() => openPackagePreview(booking)}
                  sx={{ ...textLinkSx, mt: 1 }}
                >
                  {booking.package_title}
                </Box>
                <Typography sx={{ mt: 1, fontSize: 13.5 }}>{travelDates(booking)}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 1 }}>
                  <Typography sx={{ fontSize: 13.5 }}>{formatMoney(booking.total_price)}</Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: 12.5, color: booking.status === 'pending' ? ALERT : DOCUMENT.ink2 }}>
                      {getStatusLabel(booking.status)}
                    </Typography>
                    {renderConfirmButton(booking)}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  {renderRowActions(booking)}
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              borderTop: `1px solid ${DOCUMENT.ruleHard}`,
              borderBottom: `1px solid ${DOCUMENT.ruleHard}`,
              mt: 3.25,
            }}
          >
            {[
              [t('partnerLists.grossYear'), formatMoney(totals.gross)],
              [t('partnerLists.yourPayout'), formatMoney(totals.payout)],
              [t('partnerLists.commission'), formatMoney(totals.commission)],
              [t('partnerLists.awaitingAmount'), formatMoney(totals.awaiting)],
            ].map(([label, value], index) => (
              <Box
                key={label}
                sx={{
                  flex: { xs: '1 1 50%', md: 1 },
                  px: 2.75,
                  py: 1.7,
                  borderRight: { md: index < 3 ? `1px solid ${DOCUMENT.rule}` : 0 },
                  borderBottom: { xs: index < 2 ? `1px solid ${DOCUMENT.rule}` : 0, md: 0 },
                }}
              >
                <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3, mb: 0.75 }}>{label}</Typography>
                <Typography sx={{ fontSize: 19, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: 12.5, color: DOCUMENT.ink2, lineHeight: 1.65, maxWidth: '72ch' }}>
            {t('partnerLists.payoutExplain')}
          </Typography>
        </>
      )}

      {/* Confirm Booking Dialog */}
      <Dialog 
        open={confirmDialog} 
        onClose={() => !confirming && setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          ✅ Confirm Booking
        </DialogTitle>
        <DialogContent>
          {paymentLinkGenerated ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Payment Link Generated!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The customer has been emailed a payment link. They have 48 hours to complete payment.
              </Typography>
              
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Payment Link (also sent to customer):
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      flexGrow: 1, 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis',
                      fontFamily: 'monospace',
                      fontSize: '0.8rem'
                    }}
                  >
                    {paymentLinkGenerated}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => copyToClipboard(paymentLinkGenerated)}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            </Box>
          ) : (
            <>
              {selectedBooking && (
                <Box sx={{ mb: 3 }}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Customer</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedBooking.user_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Package</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedBooking.package_title}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Travelers</Typography>
                        <Typography variant="body1">
                          {selectedBooking.num_travelers || 1}
                        </Typography>
                      </Grid>
                      {selectedBooking.travelers && (() => {
                        try {
                          const passengers = typeof selectedBooking.travelers === 'string' 
                            ? JSON.parse(selectedBooking.travelers)
                            : selectedBooking.travelers;
                          if (Array.isArray(passengers) && passengers.length > 0) {
                            return (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Passenger Details</Typography>
                                {passengers.map((p, i) => (
                                  <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {p.firstName} {p.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {[p.email, p.phone, p.nationality].filter(Boolean).join(' • ')}
                                    </Typography>
                                    {p.passportNumber && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        Passport: {p.passportNumber} {p.passportExpiry ? `(Exp: ${p.passportExpiry})` : ''}
                                      </Typography>
                                    )}
                                  </Paper>
                                ))}
                              </Grid>
                            );
                          }
                        } catch (e) {}
                        return null;
                      })()}
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Requested Amount</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                          ${selectedBooking.total_price?.toLocaleString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <TextField
                    fullWidth
                    label="Your Confirmation ID *"
                    value={confirmationId}
                    onChange={(e) => setConfirmationId(e.target.value)}
                    placeholder="e.g., CONF-12345"
                    helperText="Enter your internal booking reference number"
                    sx={{ mb: 2 }}
                  />

                  <NumericTextField
                    fullWidth
                    label="Adjust Amount (optional)"
                    value={customAmount}
                    onValueChange={setCustomAmount}
                    allowDecimal
                    allowEmpty
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    helperText="Modify if the final price differs from the quote"
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Notes for Customer (optional)"
                    value={confirmNotes}
                    onChange={(e) => setConfirmNotes(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="e.g., Special instructions, what to bring, etc."
                  />

                  <Alert severity="info" sx={{ mt: 2 }}>
                    💳 A secure Stripe payment link will be generated and emailed to the customer.
                    After payment, their itinerary will be automatically created.
                  </Alert>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            {paymentLinkGenerated ? 'Close' : 'Cancel'}
          </Button>
          {!paymentLinkGenerated && (
            <Button
              variant="contained"
              onClick={handleConfirmBooking}
              disabled={!confirmationId.trim() || confirming}
              startIcon={confirming ? <CircularProgress size={20} /> : <Payment />}
            >
              Confirm & Send Payment Link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <DocumentPageOverlay
        open={previewOpen}
        onClose={closePackagePreview}
        title={t('partnerLists.preview')}
        testId="package-preview-modal"
        actions={(
          <Button
            size="small"
            onClick={() => window.open(`/packages/${previewSlug}`, '_blank')}
            disabled={!previewSlug}
            sx={documentOverlayActionSx}
          >
            <OpenInNew sx={{ fontSize: 16, mr: 0.5 }} />
            {t('partnerLists.openTab')}
          </Button>
        )}
      >
        {previewSlug ? (
          <PublicPackageDetails previewSlug={previewSlug} isPreviewMode />
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">{t('partnerLists.previewFailed')}</Typography>
          </Box>
        )}
      </DocumentPageOverlay>

      <PartnerBookingDetailsDialog
        open={detailsModal}
        booking={detailsBooking}
        onClose={closeBookingDetails}
        onPackageClick={openPackagePreview}
        onMessage={openInquiry}
        messagingBusy={openingThreadId === detailsBooking?.id}
        onCopyPaymentLink={copyToClipboard}
        onConfirm={(booking) => {
          setDetailsModal(false);
          openConfirmDialog(booking);
        }}
      />
    </PartnerPageShell>
  );
};

export default PartnerBookings;

